"use client";

import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  APP_RESUME_REQUEST_EVENT,
  emitAppResume,
  type AppResumeDetail,
} from "@/lib/appLifecycle";
import { buildLoginHref } from "@/lib/authRedirect";
import {
  beginAuthSessionResumeGate,
  reconcileAuthSession,
  releaseAuthSessionResumeGate,
} from "@/lib/fetchWithAuth";

type GateStatus = "validating" | "ready" | "unavailable" | "redirecting";

function currentReturnPath(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function SessionCheckingView() {
  return (
    <div
      className="flex h-[100dvh] w-full items-center justify-center bg-background"
      role="status"
      aria-live="polite"
    >
      <div className="flex flex-col items-center gap-3 text-center">
        <div className="h-7 w-7 animate-spin rounded-full border-2 border-seexr-primary border-t-transparent" />
        <p className="text-sm font-medium text-muted-foreground">
          로그인 상태를 확인하고 있습니다.
        </p>
      </div>
    </div>
  );
}

/**
 * Sidebar 아래 화면은 browser history/bfcache에서 과거 React 상태가 먼저
 * 복원될 수 있다. 기존 APP_RESUME_EVENT 소비자들을 개별 수정하는 대신,
 * 이 경계에서 세션을 먼저 reconcile한 뒤 기존 resume 이벤트를 전달한다.
 *
 * 첫 진입은 검증이 끝날 때까지 children을 mount하지 않아 초기 protected
 * request fan-out을 막고, 이미 열린 화면의 resume은 children을 유지한 채
 * overlay로 입력만 막아서 진행 중인 chat/run 상태를 불필요하게 버리지 않는다.
 */
export function ProtectedSessionGate({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<GateStatus>("validating");
  const [validatedOnce, setValidatedOnce] = useState(false);
  const mountedRef = useRef(false);
  const reconcilePromiseRef = useRef<Promise<void> | null>(null);
  const pendingResumeRef = useRef<AppResumeDetail | null>(null);

  const reconcile = useCallback((resumeDetail?: AppResumeDetail) => {
    if (resumeDetail) {
      // popstate/pageshow 이후 새 route effect가 먼저 실행되더라도 authenticated
      // request는 session reconcile이 끝날 때까지 fetchWithAuth에서 대기한다.
      beginAuthSessionResumeGate();
      pendingResumeRef.current = resumeDetail;
    }

    const running = reconcilePromiseRef.current;
    if (running) return running;

    const promise = (async () => {
      if (mountedRef.current) setStatus("validating");
      const result = await reconcileAuthSession();
      if (!mountedRef.current) return;

      if (result === "authenticated") {
        setValidatedOnce(true);
        setStatus("ready");
      } else if (result === "unauthenticated") {
        // Sidebar 제품 화면은 protected route다. 최초 직접 진입이든 기존
        // authenticated document의 세션 만료든 동일하게 로그인으로 복구한다.
        pendingResumeRef.current = null;
        setStatus("redirecting");
        window.location.replace(buildLoginHref(currentReturnPath()));
        return;
      } else {
        // 일시적인 API/네트워크 실패를 세션 만료로 오인해 로그아웃하지 않는다.
        // 기존 화면도 조작 가능 상태로 방치하지 않고 명시적 retry를 제공한다.
        setStatus("unavailable");
        return;
      }

      const pendingResume = pendingResumeRef.current;
      pendingResumeRef.current = null;
      if (pendingResume) {
        // ready 상태가 먼저 commit된 뒤 기존 resume 소비자에게 전달한다.
        window.requestAnimationFrame(() => {
          if (!mountedRef.current) return;
          releaseAuthSessionResumeGate();
          emitAppResume(pendingResume);
        });
      }
    })().finally(() => {
      reconcilePromiseRef.current = null;
    });

    reconcilePromiseRef.current = promise;
    return promise;
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void reconcile();

    const handleResumeRequest = (event: Event) => {
      const detail = (event as CustomEvent<AppResumeDetail>).detail;
      void reconcile(detail);
    };

    window.addEventListener(APP_RESUME_REQUEST_EVENT, handleResumeRequest);
    return () => {
      mountedRef.current = false;
      releaseAuthSessionResumeGate();
      window.removeEventListener(APP_RESUME_REQUEST_EVENT, handleResumeRequest);
    };
  }, [reconcile]);

  if (!validatedOnce) {
    if (status === "unavailable") {
      return (
        <div className="flex h-[100dvh] w-full items-center justify-center bg-background px-6">
          <div className="flex max-w-sm flex-col items-center gap-4 text-center">
            <p className="text-base font-bold text-foreground">
              서비스에 연결하지 못했습니다.
            </p>
            <p className="text-sm text-muted-foreground">
              연결이 복구되면 다시 시도해주세요. 로그인 상태는 변경하지 않았습니다.
            </p>
            <button
              type="button"
              onClick={() => void reconcile()}
              className="rounded-full bg-seexr-primary px-4 py-2 text-sm font-bold text-white"
            >
              다시 시도
            </button>
          </div>
        </div>
      );
    }
    return <SessionCheckingView />;
  }

  return (
    <div className="relative h-[100dvh] w-full">
      {children}
      {status !== "ready" && (
        <div
          className="absolute inset-0 z-[200] flex items-center justify-center bg-background/95 px-6 backdrop-blur-sm"
          aria-live="polite"
        >
          {status === "unavailable" ? (
            <div className="flex max-w-sm flex-col items-center gap-4 text-center">
              <p className="text-base font-bold text-foreground">
                서비스에 연결하지 못했습니다.
              </p>
              <p className="text-sm text-muted-foreground">
                기존 화면은 보존했습니다. 연결이 복구되면 다시 시도해주세요.
              </p>
              <button
                type="button"
                onClick={() => void reconcile()}
                className="rounded-full bg-seexr-primary px-4 py-2 text-sm font-bold text-white"
              >
                다시 시도
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 text-center" role="status">
              <div className="h-7 w-7 animate-spin rounded-full border-2 border-seexr-primary border-t-transparent" />
              <p className="text-sm font-medium text-muted-foreground">
                {status === "redirecting"
                  ? "로그인이 필요합니다. 이동하고 있습니다."
                  : "로그인 상태를 확인하고 있습니다."}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
